/** Single reel: spin start, velocity, stop, bounce */

import { Container, Graphics } from 'pixi.js';
import { ReelSymbol } from './ReelSymbol';
import { GameConfig } from '../config/GameConfig';
import { Logger } from '../utils/Logger';

const logger = new Logger('Reel');

export interface ReelConfig {
  reelIndex: number;
  symbolWidth: number;
  symbolHeight: number;
  visibleRows: number;
  strip: readonly string[];
}

/** Reel animation states */
enum ReelState {
  IDLE,
  SPINNING,
  STOPPING,
  BOUNCING,
}

/** A single vertical reel managing a strip of ReelSymbol objects */
export class Reel extends Container {
  private _symbols: ReelSymbol[] = [];
  private _config: ReelConfig;
  private _state: ReelState = ReelState.IDLE;

  // Spin state
  private _velocity: number = 0;
  private _scrollOffset: number = 0; // Fractional position in symbol units
  private _targetStopPos: number = 0;
  private _stripIndex: number = 0; // Current top symbol index in the strip

  // Stop animation
  private _stopResolve: (() => void) | null = null;
  private _bouncePhase: number = 0;
  private _bounceStartY: number = 0;

  // Mask container
  private _symbolContainer: Container;
  private _mask: Graphics;

  // Anticipation
  private _anticipation: boolean = false;

  // Constants
  private readonly TOTAL_SYMBOLS: number;
  private readonly SPIN_SPEED = GameConfig.SPIN_SPEED;
  private readonly MAX_SPEED = GameConfig.SPIN_MAX_SPEED;

  constructor(config: ReelConfig) {
    super();
    this._config = config;

    // Total symbol slots: visible + buffer above and below
    this.TOTAL_SYMBOLS = config.visibleRows + GameConfig.BUFFER_SYMBOLS * 2;

    // Create the symbol container (will be masked)
    this._symbolContainer = new Container();
    this.addChild(this._symbolContainer);

    // Create mask to clip to visible area
    this._mask = new Graphics();
    this._mask.rect(
      -config.symbolWidth / 2,
      0,
      config.symbolWidth,
      config.visibleRows * config.symbolHeight,
    );
    this._mask.fill({ color: 0xffffff });
    this.addChild(this._mask);
    this._symbolContainer.mask = this._mask;

    // Initialize symbols
    this._initSymbols();

    // Pick a random starting position
    this._stripIndex = Math.floor(Math.random() * config.strip.length);
    this._updateSymbolTextures();

    logger.debug(`Reel ${config.reelIndex} initialized with ${config.strip.length} stops`);
  }

  get isSpinning(): boolean {
    return this._state !== ReelState.IDLE;
  }

  get config(): ReelConfig {
    return this._config;
  }

  /** Enable/disable anticipation mode — slows down the visual spin speed */
  setAnticipation(enabled: boolean): void {
    this._anticipation = enabled;
    logger.debug(`Reel ${this._config.reelIndex}: anticipation ${enabled ? 'ON' : 'OFF'}`);
  }

  /** Start the reel spinning */
  startSpin(): void {
    if (this._state !== ReelState.IDLE) return;

    this._state = ReelState.SPINNING;
    this._velocity = 0;
    this._scrollOffset = 0;

    logger.debug(`Reel ${this._config.reelIndex}: spin started`);
  }

  /** Stop the reel at the given strip position with bounce animation */
  stopAt(stopPosition: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this._state !== ReelState.SPINNING) {
        resolve();
        return;
      }

      // SNAP to 3 symbols before target, then decelerate through the last few
      // This prevents scrolling through 100+ symbols which takes forever
      const strip = this._config.strip;
      const landingOffset = 3; // decelerate through last 3 symbols
      const snapTarget = (stopPosition - landingOffset + strip.length) % strip.length;
      this._stripIndex = snapTarget;
      this._scrollOffset = 0;
      this._updateSymbolTextures();

      this._state = ReelState.STOPPING;
      this._targetStopPos = stopPosition;
      this._stopResolve = resolve;

      logger.debug(`Reel ${this._config.reelIndex}: stopping at position ${stopPosition}`);
    });
  }

  /** Instantly snap to a position without animation (for hidden/math-only reels) */
  snapTo(stopPosition: number): void {
    this._stripIndex = stopPosition;
    this._scrollOffset = 0;
    this._state = ReelState.IDLE;
    this._velocity = 0;
    this._symbolContainer.y = 0;
    this._updateSymbolTextures();
  }

  /** Get the currently visible symbol IDs (top to bottom) */
  getVisibleSymbols(): string[] {
    const strip = this._config.strip;
    const result: string[] = [];
    for (let r = 0; r < this._config.visibleRows; r++) {
      const idx = (this._stripIndex + r) % strip.length;
      result.push(strip[idx]);
    }
    return result;
  }

  /** Update reel animation (called per frame with delta in ms/16.67) */
  update(delta: number): void {
    switch (this._state) {
      case ReelState.SPINNING:
        this._updateSpinning(delta);
        break;
      case ReelState.STOPPING:
        this._updateStopping(delta);
        break;
      case ReelState.BOUNCING:
        this._updateBouncing(delta);
        break;
      case ReelState.IDLE:
        // Nothing to do
        break;
    }
  }

  private _initSymbols(): void {
    for (let i = 0; i < this.TOTAL_SYMBOLS; i++) {
      const sym = new ReelSymbol(this._config.symbolWidth, this._config.symbolHeight);
      // Position: offset by buffer amount so visible area starts at y=0
      const row = i - GameConfig.BUFFER_SYMBOLS;
      sym.y = row * this._config.symbolHeight + this._config.symbolHeight / 2;
      this._symbolContainer.addChild(sym);
      this._symbols.push(sym);
    }
  }

  /** Update symbol textures based on current strip index */
  private _updateSymbolTextures(): void {
    const strip = this._config.strip;
    for (let i = 0; i < this.TOTAL_SYMBOLS; i++) {
      const row = i - GameConfig.BUFFER_SYMBOLS;
      const stripIdx = ((this._stripIndex + row) % strip.length + strip.length) % strip.length;
      this._symbols[i].setSymbol(strip[stripIdx]);
    }
  }

  private _updateSpinning(delta: number): void {
    // Anticipation mode runs at 40% of max speed for dramatic effect
    const effectiveMaxSpeed = this._anticipation ? this.MAX_SPEED * 0.4 : this.MAX_SPEED;

    // Accelerate to max speed (or decelerate to anticipation speed)
    if (this._velocity < effectiveMaxSpeed) {
      this._velocity = Math.min(this._velocity + 2 * delta, effectiveMaxSpeed);
    } else if (this._velocity > effectiveMaxSpeed) {
      // Smoothly decelerate to anticipation speed
      this._velocity = Math.max(this._velocity - 1.5 * delta, effectiveMaxSpeed);
    }

    // Move position downward (in symbol units)
    this._scrollOffset += this._velocity * delta * 0.05;

    // When position advances past 1 symbol, shift the strip index
    while (this._scrollOffset >= 1) {
      this._scrollOffset -= 1;
      this._stripIndex = (this._stripIndex + 1) % this._config.strip.length;
      this._updateSymbolTextures();
    }

    // Apply offset to symbol container
    this._symbolContainer.y = this._scrollOffset * this._config.symbolHeight;
  }

  private _updateStopping(delta: number): void {
    // Decelerate
    this._velocity = Math.max(this._velocity - GameConfig.SPIN_DECEL_RATE * delta, this.SPIN_SPEED * 0.3);

    this._scrollOffset += this._velocity * delta * 0.05;

    while (this._scrollOffset >= 1) {
      this._scrollOffset -= 1;
      this._stripIndex = (this._stripIndex + 1) % this._config.strip.length;

      // Check if we've reached the target
      if (this._stripIndex === this._targetStopPos) {
        // Snap to position and start bounce
        this._scrollOffset = 0;
        this._symbolContainer.y = 0;
        this._updateSymbolTextures();
        this._startBounce();
        return;
      }

      this._updateSymbolTextures();
    }

    this._symbolContainer.y = this._scrollOffset * this._config.symbolHeight;
  }

  private _startBounce(): void {
    this._state = ReelState.BOUNCING;
    this._bouncePhase = 0;
    this._bounceStartY = 0;
  }

  private _updateBouncing(delta: number): void {
    const bounceDuration = GameConfig.REEL_BOUNCE_DURATION / 16.67; // Convert ms to frames
    this._bouncePhase += delta;

    if (this._bouncePhase >= bounceDuration) {
      // Bounce complete
      this._symbolContainer.y = 0;
      this._state = ReelState.IDLE;
      this._velocity = 0;

      if (this._stopResolve) {
        this._stopResolve();
        this._stopResolve = null;
      }
      return;
    }

    // Elastic bounce: overshoot down then snap back
    const t = this._bouncePhase / bounceDuration;
    const overshoot = GameConfig.REEL_BOUNCE_OVERSHOOT_PX;

    // Simple overshoot + bounce: goes down by overshoot, then back to 0
    // Using a sin curve for smooth bounce
    const bounceY = overshoot * Math.sin(t * Math.PI) * (1 - t);
    this._symbolContainer.y = bounceY;
  }
}
